import {
  Badge,
  Dropdown,
  FlexBox,
  Icon,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ConversationLanguageCode } from 'lib-shared-languages';
import { ComponentProps, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { AiLoadingState } from 'components/aiLoadingState';
import { TranslateTextChangeData } from 'components/translateTextDropdownSelectionList';

import { DropdownContent } from './components/dropdownContent/dropdownContent';
import { ErrorDropdownContent } from './components/errorDropdownContent/errorDropdownContent';
import styles from './translationBadge.module.scss';

type TranslationBadgeProps = {
  /** The successful translation data object. */
  translation?: TranslateTextChangeData;
  /** The error message shown in a tooltip when translation fails. */
  errorMessage?: string;
  /** The language of the message. */
  originalLanguageCode?: ConversationLanguageCode;
};

const knownBackendErrorMessages = {
  content_management_policy_error: 'Explicit language',
  token_limit_error: null,
};

const getKnownBackendErrorMessage = (key: string | undefined): string => {
  if (!key) return '';
  return knownBackendErrorMessages[key] || '';
};

const badgeProps: Omit<ComponentProps<typeof Badge>, 'children'> = {
  size: 'small',
  variant: 'info',
};

/** Represents a component for displaying a badge for translation. */
export const TranslationBadge = (
  props: TranslationBadgeProps,
): JSX.Element | null => {
  const { translation, errorMessage } = props;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  const isError = translation?.status === 'rejected';
  const isFulfilled = translation?.status === 'fulfilled';
  const isNotNeeded = translation?.status === 'notNeeded';

  const badgeFinalContent = useMemo(() => {
    if (isError) {
      const detailCode = translation?.errorDetailCode;
      const reason = getKnownBackendErrorMessage(detailCode);
      return `Translation failed${reason ? `: ${reason}` : ''}`;
    }

    if (isFulfilled) {
      return 'Translated';
    }

    if (isNotNeeded) {
      return translation.selectedLanguage?.name
        ? `Already in ${translation.selectedLanguage?.name}`
        : 'Translation not needed';
    }

    return '';
  }, [isError, isFulfilled, isNotNeeded, translation]);

  useClickAway(ref, () => setIsDropdownOpen(false));

  if (translation?.status === 'uninitialized') {
    return null;
  }

  if (translation?.status === 'pending') {
    return (
      <Badge {...badgeProps}>
        <AiLoadingState label="Translating" />
      </Badge>
    );
  }

  const DropdownContentComponent = isError
    ? ErrorDropdownContent
    : DropdownContent;

  return (
    <span ref={ref} className={styles.root}>
      <Tooltip
        className={styles.tooltip}
        disabled={!isError || !errorMessage}
        content={errorMessage}
      >
        <Badge
          {...badgeProps}
          variant={isError ? 'error' : 'info'}
          className={styles.badge}
          onClick={() => setIsDropdownOpen((state) => !state)}
        >
          <FlexBox gap="100" alignItems="center">
            {badgeFinalContent}
            <Icon name="angle-down" size={12 as 16} />
          </FlexBox>
        </Badge>
      </Tooltip>
      <Dropdown
        closeOnBlur
        fixed
        isOpen={isDropdownOpen}
        style={{ width: 200 }}
      >
        <DropdownContentComponent
          feedbackId={translation?.feedbackId || ''}
          onAdditionalFeedback={() => setIsDropdownOpen(false)}
        />
      </Dropdown>
    </span>
  );
};
